'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { Container } from '@eliadmin/core/src/components/ui/container';
import {
  useFormValidationMessages,
  type FormValidationMessages,
} from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { ProductDetailsWithSlug } from '@eliadmin/core/src/stores/variant-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { z } from 'zod';
import { useForm, type Control, type FieldPath } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input, type InputProps } from '@eliadmin/core/src/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@eliadmin/core/src/components/ui/select';
import { useMemo } from 'react';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { sendQuestionAboutProduct } from '@eliadmin/core/src/api/proform/proform-api';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

export const questionSchema = (VM: FormValidationMessages) =>
  z.object({
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    customer_name: z.string().min(3, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    text: z.string().min(10, VM.MIN_LENGTH_VALIDATION_MESSAGE(10)),
    gdpr: z.boolean().refine(value => value, VM.TOS_VALIDATION_MESSAGE()),
  });

export type QuestionFormValues = z.infer<ReturnType<typeof questionSchema>>;

export function ProductTabQuestionForm() {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const { toast } = useToast();
  const product = useVariantsStore(s => s.product) as ProductDetailsWithSlug & {
    metadata: {
      smako_tech_info: {
        custom_title: string;
        url: string;
        id: number;
        name: string;
      }[];
    };
  };

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof questionSchema>>>({
    defaultValues: {
      email: '',
      customer_name: '',
      text: '',
      gdpr: false,
    },
    resolver: zodResolver(questionSchema(formValidationMessages)),
  });

  const handleSubmit = form.handleSubmit(data => {
    sendQuestionAboutProduct(
      {
        ...data,
        product,
        product_url: window.location.href,
      },
      locale,
    )
      .then(() => {
        toast({
          title: t('text1042'),
          variant: 'success',
        });
      })
      .catch((e: any) => {
        toast({
          title: t('text1043'),
          description: (e as any).message,
          variant: 'destructive',
        });
      });
  });

  return (
    <Container className={cn('flex flex-col px-2', 'ea-product-detail-tab-question-form')}>
      <h2 className="mb-4">{t('text1036')}</h2>
      <h2 className="mb-4">
        {t('text1037')}: <span className="font-bold text-foreground">{product.name}</span>
      </h2>
      <form onSubmit={handleSubmit} className={cn('flex flex-col gap-6 overflow-auto', '')}>
        <Form {...form}>
        <CaptchaFormMessage errors={form.formState.errors} />
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <Field control={form.control as any} name="email" label={t('text35')} required />
            </div>
            <div className="col-span-12 md:col-span-6">
              <Field
                control={form.control as any}
                name="customer_name"
                label={t('text1044')}
                required
              />
            </div>
            <FormField
              control={form.control}
              name="text"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'col-span-12 mt-1 px-[1px]',
                    'ea-jobportal-add-experience-dialog__value-field',
                  )}
                  required
                >
                  <FormLabel>{t('text1040')}</FormLabel>
                  <FormControl
                    className={cn('', 'ea-jobportal-add-experience-dialog__value-textarea')}
                  >
                    <Textarea {...field} rows={3} />
                  </FormControl>
                  <FormMessage
                    className={cn('', 'ea-jobportal-add-experience-dialog__value-message')}
                  />
                </FormItem>
              )}
            />
          </div>
          <div className="col-span-12">
            <Field
              control={form.control as any}
              name="gdpr"
              type="checkbox"
              label={t('text1039')}
            />
          </div>
          <Button type="submit" className="rounded-full px-8 py-1 sm:max-w-min">
            {t('text1040')}
          </Button>
        </Form>
      </form>
    </Container>
  );
}

type FieldProps = {
  control: Control<z.infer<ReturnType<typeof questionSchema>>>;
  name: FieldPath<z.infer<ReturnType<typeof questionSchema>>>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  type?: InputProps['type'] | 'textarea';
  options?: { value: string | number; label: string }[];
  isFullWidth?: boolean;
  isDisabled?: boolean;
  after?: React.ReactNode;
  help?: React.ReactNode;
};

export function Field(props: FieldProps) {
  const { t } = useTranslations();
  const content = useMemo(() => {
    if (props.type === 'select') {
      if (!props.options) {
        throw new Error('Select type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              {props.label && <FormLabel>{props.label}</FormLabel>}
              <FormControl>
                <Select
                  value={String(field.value)}
                  onValueChange={e => {
                    if (props.isDisabled) return;
                    field.onChange(e);
                  }}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={props.placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {props.options?.map(o => (
                      <SelectItem key={o.value} value={String(o.value)}>
                        {t(o.label as any)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
              <p className="text-xs text-muted-foreground">{props.help}</p>
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'checkbox') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              <FormControl>
                <div className="flex w-full flex-row items-center gap-2">
                  <Checkbox
                    {...(field as any)}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={props.isDisabled}
                    aria-disabled={props.isDisabled}
                  />
                  {props.label && <FormLabel>{props.label}</FormLabel>}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'textarea') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              {props.label && <FormLabel>{props.label}</FormLabel>}
              <FormControl>
                <div className="flex w-full flex-row items-center gap-2">
                  <Textarea
                    placeholder={props.placeholder}
                    {...(field as any)}
                    value={field.value ?? ''}
                    disabled={props.isDisabled}
                    aria-disabled={props.isDisabled}
                    rows={5}
                    className={{ wrapper: 'w-auto flex-1' }}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={props.control}
        name={props.name}
        render={({ field }) => (
          <FormItem required={props.required}>
            {props.label && <FormLabel>{props.label}</FormLabel>}
            <FormControl>
              <div className="flex w-full flex-row items-center gap-2">
                <Input
                  placeholder={props.placeholder}
                  type={props.type}
                  {...(field as any)}
                  value={field.value ?? ''}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                  afterOuter={props.after}
                  // added margins because of active ring overflowing
                  className={{ wrapper: 'm-[1px] w-auto flex-1', main: 'mt-[1px]' }}
                />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }, [
    props.after,
    props.control,
    props.isDisabled,
    props.label,
    props.name,
    props.options,
    props.placeholder,
    props.required,
    props.type,
    props.help,
    t,
  ]);

  return content;
}
